import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useMetaAPI, type Partner, type FetchMembersUsersParams, type MembersUsersResponse, type FetchMemberProductsParams, type MemberProductsResponse, type FetchCannedResponsesParams, type CannedResponsesResponse } from '@/composables/services/useMetaAPI'

// Re-export Partner type for convenience
export type { Partner } from '@/composables/services/useMetaAPI'

export const useMetaStore = defineStore('meta', () => {
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const lastRefreshTime = ref<Date | null>(null)
  const metaData = ref<any>(null)
  const partnerMetaData = ref<any>(null)
  const cannedResponses = ref<any>(null)
  const allPartners = ref<Partner[]>([])
  const allPartnerTeams = ref<any[]>([])
  // Cache of users fetched from pl__partners_teams_users responses (keyed by user id).
  // Components that fetch team-scoped users should call cacheTeamsUsers() so the data
  // is available globally (e.g. for avatar lookups across the app).
  const teamsUsersCache = ref<Map<string, any>>(new Map())

  // Track pending promises to prevent duplicate fetches
  let metaDataPromise: Promise<void> | null = null
  let partnerMetaDataPromise: Promise<void> | null = null

  // Computed properties for easy access to partner data
  const partnerIds = computed(() => allPartners.value.map(partner => partner.id))
  const partnerAvatars = computed(() => {
    const avatarMap: Record<string, string> = {}
    allPartners.value.forEach(partner => {
      avatarMap[partner.id] = partner.url_avatar || partner.avatar
    })
    return avatarMap
  })

  // Computed properties for easy access to user data
  const usersMap = computed(() => {
    const map = new Map<string, any>()
    if (partnerMetaData.value?.pl__partners_users) {
      partnerMetaData.value.pl__partners_users.forEach((user: any) => {
        const userId = user.id || user.val
        if (userId) {
          map.set(userId, {
            id: userId,
            name: user.lbl || user.name,
            email: user.email,
            avatar: user.url_avatar || user.avatar,
            ...user
          })
        }
      })
    }
    return map
  })

  const userAvatars = computed(() => {
    const avatarMap: Record<string, string> = {}

    if (partnerMetaData.value?.pl__partners_users) {
      partnerMetaData.value.pl__partners_users.forEach((user: any) => {
        const userId = user.id || user.val
        const avatar = user.url_avatar || user.avatar
        if (userId && avatar) {
          avatarMap[userId] = avatar
        }
      })
    }

    // Merge in avatars from team-scoped user fetches (pl__partners_teams_users),
    // which reliably include url_avatar unlike the basic partners_users list.
    teamsUsersCache.value.forEach((user: any, userId: string) => {
      const avatar = user.url_avatar || user.avatar
      if (avatar) {
        avatarMap[userId] = avatar
      }
    })

    return avatarMap
  })

  /**
   * Cache users from a pl__partners_teams_users response into the global store.
   * Call this whenever fetchPartnersTeamsUsers() returns data so that avatar URLs
   * are available app-wide without additional network requests.
   */
  function cacheTeamsUsers(users: Array<any>) {
    users.forEach((user: any) => {
      const userId = user.id || user.val
      if (userId) {
        teamsUsersCache.value.set(userId, user)
      }
    })
  }

  // Computed property for password policies
  const passwordPolicies = computed(() => {
    if (partnerMetaData.value?.pl__partners_password_policies) {
      return partnerMetaData.value.pl__partners_password_policies
    }
    return []
  })

  async function loadMetaData() {
    // If already loaded, skip
    if (metaData.value) {
      return
    }

    // If currently loading, return existing promise
    if (metaDataPromise) {
      return metaDataPromise
    }

    // Start new fetch
    metaDataPromise = (async () => {
      try {
        isLoading.value = true
        error.value = null
        
        const metaAPI = useMetaAPI()
        const response = await metaAPI.fetchMetaData()
        metaData.value = response
        lastRefreshTime.value = new Date()
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to load meta data'
        console.error('🏭 MetaStore: Error loading meta data:', err)
        throw err
      } finally {
        isLoading.value = false
        metaDataPromise = null
      }
    })()

    return metaDataPromise
  }

  async function loadPartnerMetaData() {
    // If already loaded, skip
    if (partnerMetaData.value && allPartners.value.length > 0 && allPartnerTeams.value.length > 0) {
      return
    }

    // If currently loading, return existing promise
    if (partnerMetaDataPromise) {
      return partnerMetaDataPromise
    }

    // Start new fetch
    partnerMetaDataPromise = (async () => {
      try {
        isLoading.value = true
        error.value = null
        
        const metaAPI = useMetaAPI()
        
        // Load both partner data and teams data in parallel
        const [partnerResponse, teamsResponse] = await Promise.all([
          metaAPI.fetchPartnerMetaData(),
          metaAPI.fetchPartnersTeams()
        ])
        
        partnerMetaData.value = partnerResponse
        
        // Extract and store pl__all_partners data
        if (partnerResponse?.pl__all_partners && Array.isArray(partnerResponse.pl__all_partners)) {
          allPartners.value = partnerResponse.pl__all_partners as Partner[]
        } else {
          console.warn('📊 Meta: No pl__all_partners found in response')
          allPartners.value = []
        }
        
        // Extract and store pl__all_partners_teams data
        if (teamsResponse?.pl__all_partners_teams && Array.isArray(teamsResponse.pl__all_partners_teams)) {
          allPartnerTeams.value = teamsResponse.pl__all_partners_teams
          
          // Also merge the teams data into partnerMetaData for backward compatibility
          partnerMetaData.value.pl__all_partners_teams = teamsResponse.pl__all_partners_teams
          partnerMetaData.value.pl__partners_teams = teamsResponse.pl__partners_teams
          partnerMetaData.value.pl__org_partners_teams = teamsResponse.pl__org_partners_teams
        } else {
          console.warn('📊 Meta: No pl__all_partners_teams found in teams response')
          allPartnerTeams.value = []
        }
        
        lastRefreshTime.value = new Date()
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to load partner meta data'
        console.error('Error loading partner meta data:', err)
        throw err
      } finally {
        isLoading.value = false
        partnerMetaDataPromise = null
      }
    })()

    return partnerMetaDataPromise
  }

  function getPartnerLabelById(partnerId: string): string {
    if (!partnerMetaData.value || !partnerMetaData.value.pl__partners_users) {
        return '';
    }

    const partner = partnerMetaData.value.pl__partners_users.find((partner: any) => partner.id === partnerId);

    return partner ? partner.lbl || '' : '';
  }

  // Helper functions for pl__all_partners data
  function getPartnerById(partnerId: string): Partner | null {
    return allPartners.value.find(partner => partner.id === partnerId) || null
  }

  function getPartnerAvatarById(partnerId: string): string {
    const partner = getPartnerById(partnerId)
    return partner?.url_avatar || partner?.avatar || ''
  }

  function getPartnerNameById(partnerId: string): string {
    const partner = getPartnerById(partnerId)
    return partner?.lbl || ''
  }

  // Helper functions for pl__all_partners_teams data
  function getTeamById(teamId: string): any | null {
    return allPartnerTeams.value.find(team => team.id === teamId || team.val === teamId) || null
  }

  function getTeamNameById(teamId: string): string {
    const team = getTeamById(teamId)
    return team?.lbl || ''
  }

  function getTeamsByPartnerId(partnerId: string): any[] {
    return allPartnerTeams.value.filter(team => team.partners_id === partnerId)
  }

  function getPasswordPolicyById(policyId: number): any | null {
    if (!partnerMetaData.value?.pl__partners_password_policies) {
      return null
    }
    return partnerMetaData.value.pl__partners_password_policies.find(
      (policy: any) => policy.id === policyId || policy.val === policyId
    ) || null
  }

  function getPasswordPolicyLabelById(policyId: number): string {
    const policy = getPasswordPolicyById(policyId)
    return policy?.lbl || ''
  }

  async function reload() {
    return Promise.all([loadMetaData(), loadPartnerMetaData()])
  }

  async function fetchMembersUsers(params: FetchMembersUsersParams): Promise<MembersUsersResponse> {
    try {
      const metaAPI = useMetaAPI()
      return await metaAPI.fetchMembersUsers(params)
    } catch (err) {
      console.error('Error fetching members users:', err)
      throw err
    }
  }

  async function fetchMemberProducts(params: FetchMemberProductsParams): Promise<MemberProductsResponse> {
    try {
      const metaAPI = useMetaAPI()
      return await metaAPI.fetchMemberProducts(params)
    } catch (err) {
      console.error('Error fetching member products:', err)
      throw err
    }
  }

  async function fetchCannedResponses(params: FetchCannedResponsesParams): Promise<CannedResponsesResponse> {
    try {
      const metaAPI = useMetaAPI()
      const response = await metaAPI.fetchCannedResponses(params)
      
      // Normalize the response to ensure we have pl__canned_responses
      if (response.results && !response.pl__canned_responses) {
        response.pl__canned_responses = response.results
      }
      
      cannedResponses.value = response
      return response
    } catch (err) {
      console.error('Error fetching canned responses:', err)
      throw err
    }
  }

  function clearMetaData() {
    metaData.value = null
    partnerMetaData.value = null
    cannedResponses.value = null
    allPartners.value = []
    allPartnerTeams.value = []
    error.value = null
    lastRefreshTime.value = null
  }

  /**
   * Force refresh partner metadata (users and teams)
   * 
   * This clears the cache and re-fetches metaPartners and metaPartnersTeams data.
   * Call this after successful updates to:
   * - Users (adding/editing/removing users)
   * - Teams (adding/editing/removing teams)
   * - Roles & Permissions (changing user roles)
   * - General organization info (if it affects partner/team data)
   * 
   * Example usage in settings components:
   * ```
   * try {
   *   await saveUserSettings()
   *   await metaStore.forceRefreshPartnerMetaData()
   * } catch (error) {
   *   console.error('Error:', error)
   * }
   * ```
   */
  async function forceRefreshPartnerMetaData() {
    // Clear the cached data to force a fresh fetch
    partnerMetaData.value = null
    allPartners.value = []
    allPartnerTeams.value = []
    
    // Reset the promise to allow a new fetch
    partnerMetaDataPromise = null
    
    // Trigger fresh fetch
    return loadPartnerMetaData()
  }

  return {
    isLoading,
    error,
    lastRefreshTime,
    metaData,
    partnerMetaData,
    cannedResponses,
    allPartners,
    allPartnerTeams,
    teamsUsersCache,
    partnerIds,
    partnerAvatars,
    usersMap,
    userAvatars,
    passwordPolicies,
    cacheTeamsUsers,
    loadMetaData,
    loadPartnerMetaData,
    forceRefreshPartnerMetaData,
    getPartnerLabelById,
    getPartnerById,
    getPartnerAvatarById,
    getPartnerNameById,
    getTeamById,
    getTeamNameById,
    getTeamsByPartnerId,
    getPasswordPolicyById,
    getPasswordPolicyLabelById,
    reload,
    fetchMembersUsers,
    fetchMemberProducts,
    fetchCannedResponses,
    clearMetaData
  }
});
